/**
 * Custom wishlist feature (Liquid + JS + CSS + localStorage only, no app).
 *
 * Guests get a browser-local wishlist under a "guest" storage key. Logged-in
 * customers get their own key namespaced by customer id, so their wishlist
 * keeps working across sessions on this browser/device; anything added while
 * signed out is merged in automatically the next time they log in. There is
 * no backend here, so this does not sync across devices/browsers.
 */
(function () {
  'use strict';

  var THEME = window.theme || {};
  var WISHLIST_CONFIG = THEME.wishlist || {};
  var MONEY_FORMAT = (THEME.settings && THEME.settings.moneyWithCurrencyFormat) || '${{amount}}';
  var CUSTOMER_ID = WISHLIST_CONFIG.customerId || null;

  var GUEST_KEY = 'oneside:wishlist:guest';
  var CUSTOMER_KEY_PREFIX = 'oneside:wishlist:customer:';
  var POPUP_AUTOHIDE_MS = 5000;

  var HEART_SVG = '<svg class="wishlist-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z"></path></svg>';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = isNaN(precision) ? 2 : precision;
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;

    if (isNaN(number)) return '0';

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  function formatMoney(cents, format) {
    cents = Number(cents || 0);
    var formatString = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';
    var value;

    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function sameItem(a, b) {
    return String(a.productId) === String(b.productId) && String(a.variantId) === String(b.variantId);
  }

  function WishlistStore() {
    this.listeners = [];
    this._migrateGuestItems();
  }

  WishlistStore.prototype.getStorageKey = function () {
    return CUSTOMER_ID ? CUSTOMER_KEY_PREFIX + CUSTOMER_ID : GUEST_KEY;
  };

  WishlistStore.prototype._migrateGuestItems = function () {
    if (!CUSTOMER_ID) return;

    try {
      var guestRaw = localStorage.getItem(GUEST_KEY);
      if (!guestRaw) return;

      var guestItems = JSON.parse(guestRaw) || [];
      if (!guestItems.length) {
        localStorage.removeItem(GUEST_KEY);
        return;
      }

      var items = this._read();
      guestItems.forEach(function (item) {
        var exists = items.some(function (existing) {
          return sameItem(existing, item);
        });
        if (!exists) items.push(item);
      });

      this._write(items, true);
      localStorage.removeItem(GUEST_KEY);
    } catch (e) {
      /* localStorage unavailable or corrupt data - ignore */
    }
  };

  WishlistStore.prototype._read = function () {
    try {
      return JSON.parse(localStorage.getItem(this.getStorageKey()) || '[]');
    } catch (e) {
      return [];
    }
  };

  WishlistStore.prototype._write = function (items, silent) {
    localStorage.setItem(this.getStorageKey(), JSON.stringify(items));
    if (!silent) this._emit();
  };

  WishlistStore.prototype._emit = function () {
    var items = this._read();
    this.listeners.forEach(function (fn) {
      try {
        fn(items);
      } catch (e) {
        /* listener error shouldn't break the store */
      }
    });
  };

  WishlistStore.prototype.onChange = function (fn) {
    this.listeners.push(fn);
  };

  WishlistStore.prototype.getItems = function () {
    return this._read();
  };

  WishlistStore.prototype.count = function () {
    return this._read().length;
  };

  WishlistStore.prototype.has = function (productId, variantId) {
    return this._read().some(function (item) {
      return String(item.productId) === String(productId) && String(item.variantId) === String(variantId);
    });
  };

  WishlistStore.prototype.add = function (item) {
    var items = this._read();
    if (items.some(function (existing) { return sameItem(existing, item); })) return false;
    items.unshift(item);
    this._write(items);
    return true;
  };

  WishlistStore.prototype.remove = function (productId, variantId) {
    var items = this._read();
    var next = items.filter(function (item) {
      return !(String(item.productId) === String(productId) && String(item.variantId) === String(variantId));
    });
    if (next.length === items.length) return false;
    this._write(next);
    return true;
  };

  // Returns true if the item is now in the wishlist, false if it was just removed.
  WishlistStore.prototype.toggle = function (item) {
    if (this.has(item.productId, item.variantId)) {
      this.remove(item.productId, item.variantId);
      return false;
    }
    this.add(item);
    return true;
  };

  function WishlistUI(store) {
    this.store = store;
    this.popupTimer = null;

    this._bindEvents();
    var self = this;
    this.store.onChange(function () { self.syncAll(); });
    window.addEventListener('storage', function (e) {
      if (e.key === self.store.getStorageKey()) self.syncAll();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { self.syncAll(); });
    } else {
      self.syncAll();
    }
  }

  WishlistUI.prototype._readItemFromButton = function (button) {
    return {
      productId: button.getAttribute('data-product-id'),
      variantId: button.getAttribute('data-variant-id'),
      handle: button.getAttribute('data-handle') || '',
      title: button.getAttribute('data-title') || '',
      url: button.getAttribute('data-url') || '',
      image: button.getAttribute('data-image') || '',
      price: Number(button.getAttribute('data-price') || 0),
      compareAtPrice: button.getAttribute('data-compare-at-price') ? Number(button.getAttribute('data-compare-at-price')) : null,
      available: button.getAttribute('data-available') === 'true',
      addedAt: Date.now()
    };
  };

  WishlistUI.prototype._bindEvents = function () {
    var self = this;

    document.addEventListener('click', function (e) {
      var toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        self.handleToggle(toggleBtn);
        return;
      }

      var openBtn = e.target.closest('[data-wishlist-open]');
      if (openBtn) {
        e.preventDefault();
        self.openDrawer();
        return;
      }

      var closeBtn = e.target.closest('[data-wishlist-close]');
      if (closeBtn) {
        e.preventDefault();
        self.closeDrawer();
        return;
      }

      if (e.target.closest('[data-wishlist-overlay]')) {
        self.closeDrawer();
        return;
      }

      var popupClose = e.target.closest('[data-wishlist-popup-close]');
      if (popupClose) {
        e.preventDefault();
        self.hidePopup();
        return;
      }

      var moveToCartBtn = e.target.closest('[data-wishlist-move-to-cart]');
      if (moveToCartBtn) {
        e.preventDefault();
        e.stopPropagation();
        self.handleMoveToCart(moveToCartBtn);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        self.closeDrawer();
        self.hidePopup();
      }
    });
  };

  WishlistUI.prototype.handleToggle = function (button) {
    var item = this._readItemFromButton(button);
    if (!item.productId || !item.variantId) return;

    var isNowActive = this.store.toggle(item);
    this.showPopup(item, isNowActive);
  };

  WishlistUI.prototype.handleMoveToCart = function (button) {
    if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;

    var variantId = button.getAttribute('data-variant-id');
    var productId = button.getAttribute('data-product-id');
    if (!variantId || !productId) return;

    var cartDrawer = document.querySelector('cart-drawer');
    var sections = 'cart-icon-bubble';
    if (cartDrawer) {
      var sectionEl = cartDrawer.closest('.shopify-section');
      if (sectionEl && sectionEl.id) {
        sections += ',' + sectionEl.id.replace('shopify-section-', '');
      }
    }

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    button.textContent = 'Adding…';

    var self = this;
    var cartAddUrl = (THEME.routes && THEME.routes.cartAdd) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          var error = typeof result.data.description === 'string'
            ? result.data.description
            : (result.data.message || 'Could not add to cart');
          throw new Error(error);
        }

        self.store.remove(productId, variantId);

        var ProductFormClass = customElements.get('product-form');
        if (ProductFormClass && typeof ProductFormClass.updateCartIcon === 'function') {
          ProductFormClass.updateCartIcon(result.data);
        } else if (result.data.sections && result.data.sections['cart-icon-bubble']) {
          var cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            cartIconBubble.innerHTML = result.data.sections['cart-icon-bubble'];
          }
        }

        self.closeDrawer();

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(result.data, true);
        } else {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        }

        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: Number(variantId) }
        }));
      })
      .catch(function (error) {
        button.textContent = 'Move to cart';
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
        window.alert(error.message || 'Could not add to cart');
      });
  };

  WishlistUI.prototype.syncAll = function () {
    this.syncButtons();
    this.syncBadges();
    this.renderLists();
  };

  WishlistUI.prototype.syncButtons = function () {
    var store = this.store;
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      var active = store.has(button.getAttribute('data-product-id'), button.getAttribute('data-variant-id'));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active
        ? (button.getAttribute('data-label-remove') || 'Remove from wishlist')
        : (button.getAttribute('data-label-add') || 'Add to wishlist');
      button.setAttribute('aria-label', label);
    });
  };

  WishlistUI.prototype.syncBadges = function () {
    var count = this.store.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  };

  WishlistUI.prototype.renderLists = function () {
    var items = this.store.getItems();
    var self = this;
    document.querySelectorAll('[data-wishlist-list]').forEach(function (container) {
      self.renderList(container, items);
    });
  };

  WishlistUI.prototype.renderList = function (container, items) {
    var wrapper = container.parentElement;
    var emptyEl = wrapper ? wrapper.querySelector('[data-wishlist-empty]') : null;

    if (!items.length) {
      container.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    container.innerHTML = items.map(this.itemTemplate.bind(this)).join('');
  };

  WishlistUI.prototype.itemTemplate = function (item) {
    var money = formatMoney(item.price, MONEY_FORMAT);
    var hasCompare = item.compareAtPrice && item.compareAtPrice > item.price;
    var compareMoney = hasCompare ? formatMoney(item.compareAtPrice, MONEY_FORMAT) : '';
    var title = escapeHtml(item.title);
    var url = escapeHtml(item.url);
    var image = escapeHtml(item.image);
    var available = item.available !== false;
    var moveToCartBtn = available
      ? (
        '<button type="button" class="wishlist-item__atc btn btn--secondary btn--sm" data-wishlist-move-to-cart ' +
          'data-product-id="' + escapeHtml(item.productId) + '" ' +
          'data-variant-id="' + escapeHtml(item.variantId) + '">' +
          'Move to cart' +
        '</button>'
      )
      : '<button type="button" class="wishlist-item__atc btn btn--secondary btn--sm" disabled aria-disabled="true">Sold out</button>';

    return (
      '<div class="wishlist-item">' +
        '<a href="' + url + '" class="wishlist-item__media">' +
          '<img src="' + image + '" alt="' + title + '" loading="lazy" width="120" height="120">' +
        '</a>' +
        '<button type="button" class="wishlist-btn wishlist-btn--list is-active" data-wishlist-toggle ' +
          'data-product-id="' + escapeHtml(item.productId) + '" ' +
          'data-variant-id="' + escapeHtml(item.variantId) + '" ' +
          'data-handle="' + escapeHtml(item.handle) + '" ' +
          'data-title="' + title + '" ' +
          'data-url="' + url + '" ' +
          'data-image="' + image + '" ' +
          'data-price="' + item.price + '" ' +
          'data-compare-at-price="' + (item.compareAtPrice || '') + '" ' +
          'data-available="' + (available ? 'true' : 'false') + '" ' +
          'data-label-add="Add to wishlist" data-label-remove="Remove from wishlist" ' +
          'aria-pressed="true" aria-label="Remove from wishlist">' +
          HEART_SVG +
        '</button>' +
        '<div class="wishlist-item__info">' +
          '<a href="' + url + '" class="wishlist-item__title">' + title + '</a>' +
          '<div class="wishlist-item__price">' +
            '<span class="wishlist-item__price-current">' + money + '</span>' +
            (compareMoney ? '<span class="wishlist-item__price-compare">' + compareMoney + '</span>' : '') +
          '</div>' +
          (available ? '' : '<span class="wishlist-item__availability">Sold out</span>') +
          moveToCartBtn +
        '</div>' +
      '</div>'
    );
  };

  WishlistUI.prototype.openDrawer = function () {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) {
      window.location.href = '/pages/wishlist';
      return;
    }
    drawer.classList.add('is-open');
    document.documentElement.classList.add('wishlist-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
  };

  WishlistUI.prototype.closeDrawer = function () {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('wishlist-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
  };

  WishlistUI.prototype.showPopup = function (item, isAdded) {
    var popup = document.querySelector('[data-wishlist-popup]');
    if (!popup) return;

    var img = popup.querySelector('[data-wishlist-popup-image]');
    var title = popup.querySelector('[data-wishlist-popup-title]');
    var message = popup.querySelector('[data-wishlist-popup-message]');
    if (img) {
      img.src = item.image || '';
      img.alt = item.title || '';
    }
    if (title) title.textContent = item.title || '';
    if (message) {
      message.textContent = isAdded
        ? popup.getAttribute('data-message-add')
        : popup.getAttribute('data-message-remove');
    }

    var headerGroup = document.querySelector('.shopify-section-group-header-group') || document.querySelector('.header');
    if (headerGroup) {
      var rect = headerGroup.getBoundingClientRect();
      popup.style.top = Math.max(rect.bottom, 0) + 12 + 'px';
    }

    clearTimeout(this.popupTimer);
    popup.classList.remove('is-visible');
    void popup.offsetWidth; // restart animation
    popup.classList.add('is-visible');
    popup.setAttribute('aria-hidden', 'false');

    var self = this;
    this.popupTimer = setTimeout(function () { self.hidePopup(); }, POPUP_AUTOHIDE_MS);
  };

  WishlistUI.prototype.hidePopup = function () {
    var popup = document.querySelector('[data-wishlist-popup]');
    if (!popup) return;
    popup.classList.remove('is-visible');
    popup.setAttribute('aria-hidden', 'true');
    clearTimeout(this.popupTimer);
  };

  var store = new WishlistStore();
  var ui = new WishlistUI(store);

  window.OnesideWishlist = {
    store: store,
    ui: ui
  };
})();
